import { ListingType, PropertyType } from '@app/models/enums'
import { IListing, Listing } from '@app/models/Listing'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum ListingsScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IListingsListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IListing>
  readonly listingType: ListingType
  readonly onlyNoPhotos: boolean
  readonly primaryAgent: string
  readonly propertyTypes: List<PropertyType>
  readonly scope: ListingsScope
  readonly sortOrder: string
  readonly orderBy: string
  readonly status: string
  readonly totalItems: number
}

const ListingsListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Listing>(),
  listingType: '',
  onlyNoPhotos: false,
  primaryAgent: 'All',
  propertyTypes: List<PropertyType>(),
  scope: ListingsScope.All,
  sortOrder: 'ExpirationDate',
  orderBy: 'Ascending',
  status: 'Active',
  totalItems: 0,
})

export class ListingsListState
  extends ListingsListStateRecord
  implements IListingsListState
{
  public static fromJS(initialState?: Partial<IListingsListState>) {
    if (!initialState) {
      return new ListingsListState()
    }

    const { items, ...rest } = initialState
    return new ListingsListState({
      items: items ? List(items.map((i) => Listing.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly assignedTo: string
  public readonly dueDate: string
  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Listing>
  public readonly listingType: ListingType
  public readonly onlyNoPhotos: boolean
  public readonly primaryAgent: string
  public readonly propertyTypes: List<PropertyType>
  public readonly scope: ListingsScope
  public readonly sortOrder: string
  public readonly orderBy: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<ListingsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ListingsListActionTypes {
  LoadListings = 'LISTINGS_LIST::LOAD_LISTINGS',
  LoadListingsFailure = 'LISTINGS_LIST::LOAD_LISTINGS_FAILURE',
  LoadListingsRequest = 'LISTINGS_LIST::LOAD_LISTINGS_REQUEST',
  LoadListingsSuccess = 'LISTINGS_LIST::LOAD_LISTINGS_SUCCESS',
  SetAssignedTo = 'LISTINGS_LIST::SET_ASSIGNED_TO',
  SetDueDate = 'LISTINGS_LIST::SET_DUE_DATE',
  SetFilterText = 'LISTINGS_LIST::SET_FILTER_TEXT',
  SetListingType = 'LISTINGS_LIST::SET_LISTING_TYPE',
  SetOnlyNoPhotos = 'LISTINGS_LIST::SET_ONLY_NO_PHOTOS',
  SetPrimaryAgent = 'LISTINGS_LIST::SET_PRIMARY_AGENT',
  SetPropertyTypes = 'LISTINGS_LIST::SET_PROPERTY_TYPES',
  SetScope = 'LISTINGS_LIST::SET_SCOPE',
  SetStatus = 'LISTINGS_LIST::SET_STATUS',
  SetSortOrder = 'LISTINGS_LIST::SET_SORT_ORDER',
  SetOrderBy = 'LISTINGS_LIST::SET_ORDER_BY',
}

interface ILoadListingsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ListingsListActionTypes.LoadListings
}

interface ILoadListingsFailureAction {
  error: string
  type: ListingsListActionTypes.LoadListingsFailure
}

interface ILoadListingsRequestAction {
  type: ListingsListActionTypes.LoadListingsRequest
}

interface ILoadListingsSuccessAction {
  append: boolean
  items: Listing[]
  totalItems: number
  type: ListingsListActionTypes.LoadListingsSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: ListingsListActionTypes.SetFilterText
}

interface ISetListingTypeAction {
  listingType: string
  type: ListingsListActionTypes.SetListingType
}

interface ISetOnlyNoPhotosAction {
  onlyNoPhotos: boolean
  type: ListingsListActionTypes.SetOnlyNoPhotos
}

interface ISetPrimaryAgentAction {
  primaryAgent: string
  type: ListingsListActionTypes.SetPrimaryAgent
}

interface ISetPropertyTypesAction {
  propertyTypes: List<PropertyType>
  type: ListingsListActionTypes.SetPropertyTypes
}

interface ISetScopeAction {
  scope: ListingsScope
  type: ListingsListActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: ListingsListActionTypes.SetSortOrder
}

interface ISetOrderByAction {
  orderBy: string
  type: ListingsListActionTypes.SetOrderBy
}

interface ISetStatusAction {
  status: string
  type: ListingsListActionTypes.SetStatus
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadListingsFailureAction
  | ILoadListingsRequestAction
  | ILoadListingsSuccessAction
  | ISetFilterTextAction
  | ISetListingTypeAction
  | ISetOnlyNoPhotosAction
  | ISetPrimaryAgentAction
  | ISetPropertyTypesAction
  | ISetScopeAction
  | ISetSortOrderAction
  | ISetOrderByAction
  | ISetStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadListings(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadListingsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ListingsListActionTypes.LoadListings,
  }
}

function loadListingsFailure(error: string): ILoadListingsFailureAction {
  return {
    error,
    type: ListingsListActionTypes.LoadListingsFailure,
  }
}

function loadListingsRequest(): ILoadListingsRequestAction {
  return {
    type: ListingsListActionTypes.LoadListingsRequest,
  }
}

function loadListingsSuccess(
  items: Listing[],
  append: boolean,
  totalItems: number,
): ILoadListingsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ListingsListActionTypes.LoadListingsSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ListingsListActionTypes.SetFilterText,
  }
}

function setListingType(listingType: string): ISetListingTypeAction {
  return {
    listingType,
    type: ListingsListActionTypes.SetListingType,
  }
}

function setOnlyNoPhotos(onlyNoPhotos: boolean): ISetOnlyNoPhotosAction {
  return {
    onlyNoPhotos,
    type: ListingsListActionTypes.SetOnlyNoPhotos,
  }
}

function setPrimaryAgent(primaryAgent: string): ISetPrimaryAgentAction {
  return {
    primaryAgent,
    type: ListingsListActionTypes.SetPrimaryAgent,
  }
}

function setPropertyTypes(
  propertyTypes: List<PropertyType>,
): ISetPropertyTypesAction {
  return {
    propertyTypes,
    type: ListingsListActionTypes.SetPropertyTypes,
  }
}

function setScope(scope: ListingsScope): ISetScopeAction {
  return {
    scope,
    type: ListingsListActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: ListingsListActionTypes.SetSortOrder,
  }
}

function setOrderBy(orderBy: string) {
  return {
    orderBy,
    type: ListingsListActionTypes.SetOrderBy,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: ListingsListActionTypes.SetStatus,
  }
}

export const actionCreators = {
  loadListings,
  setFilterText,
  setListingType,
  setOnlyNoPhotos,
  setPrimaryAgent,
  setPropertyTypes,
  setScope,
  setSortOrder,
  setOrderBy,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ListingsListState> = (
  state: ListingsListState = new ListingsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ListingsListActionTypes.LoadListingsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ListingsListState

    case ListingsListActionTypes.LoadListingsRequest:
      return state.set('isLoading', true) as ListingsListState

    case ListingsListActionTypes.LoadListingsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Listing>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as ListingsListState

    case ListingsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as ListingsListState

    case ListingsListActionTypes.SetListingType:
      return state.set('listingType', action.listingType) as ListingsListState

    case ListingsListActionTypes.SetOnlyNoPhotos:
      return state.set('onlyNoPhotos', action.onlyNoPhotos) as ListingsListState

    case ListingsListActionTypes.SetPrimaryAgent:
      return state.set('primaryAgent', action.primaryAgent) as ListingsListState

    case ListingsListActionTypes.SetPropertyTypes:
      return state.set(
        'propertyTypes',
        action.propertyTypes,
      ) as ListingsListState

    case ListingsListActionTypes.SetScope:
      return state.set('scope', action.scope) as ListingsListState

    case ListingsListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as ListingsListState

    case ListingsListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as ListingsListState

    case ListingsListActionTypes.SetStatus:
      return state.set('status', action.status) as ListingsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadListingsSaga(action: ILoadListingsAction) {
  try {
    yield put(loadListingsRequest())

    const currentState: ListingsListState = yield select(
      (state: ApplicationState) => state.listingsList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.sortOrder !== 'LastName') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (currentState.listingType) {
      query.push(`type=${currentState.listingType}`)
    }

    if (currentState.propertyTypes.count() > 0) {
      query.push(`propertyTypes=${currentState.propertyTypes.join('-')}`)
    }

    if (currentState.primaryAgent) {
      query.push(`agent=${currentState.primaryAgent}`)
    }

    if (currentState.onlyNoPhotos) {
      query.push(`noPhotoOnly=${currentState.onlyNoPhotos}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl =
      currentState.scope === ListingsScope.Mine
        ? '/api/Listings/My'
        : '/api/Listings'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Listing[] = result.results.map((c: any) =>
        Listing.fromJS(c),
      )
      yield put(
        loadListingsSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadListingsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadListings() {
  yield takeLatest(ListingsListActionTypes.LoadListings, loadListingsSaga)
}

export function* listingsListRootSaga() {
  yield fork(watchLoadListings)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): ListingsListState =>
    state.listingsList

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectListingtype = () =>
  createSelector(selectModuleState(), (mState) => mState.listingType)

export const selectOnlyNoPhotos = () =>
  createSelector(selectModuleState(), (mState) => mState.onlyNoPhotos)

export const selectPrimaryAgent = () =>
  createSelector(selectModuleState(), (mState) => mState.primaryAgent)

export const selectPropertyTypes = () =>
  createSelector(selectModuleState(), (mState) => mState.propertyTypes)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectStatus = () =>
  createSelector(selectModuleState(), (mState) => mState.status)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
